/**
 * Pure display-boundary filter for the cloud catalog sidebar.
 *
 * The underlying resource snapshots (cmmdForgeRuntimeListResource) are never
 * filtered. Route resolution, thread lifecycle dispatch, and archive operations
 * all need rows from every workspace. Only the sidebar display narrows to the
 * current workspace by default.
 *
 * The "all workspaces" toggle lets the user opt back into the full view.
 */

import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import { useCurrentForgeWorkspaceState } from "~/workspace/currentForgeWorkspaceStore";

/**
 * Filter cloud project rows to the current workspace.
 * Returns all rows when:
 *   - showAllWorkspaces is true (the toggle is on)
 *   - currentWorkspaceId is null (no workspace resolved yet)
 */
export function scopeCloudProjectRows(
  rows: readonly CloudProjectCatalogRow[],
  currentWorkspaceId: number | null,
  showAllWorkspaces: boolean,
): readonly CloudProjectCatalogRow[] {
  if (showAllWorkspaces || currentWorkspaceId === null) return rows;
  return rows.filter((row) => row.cmmdOrganizationId === currentWorkspaceId);
}

/**
 * Filter cloud environment rows to the current workspace.
 * Returns all rows when:
 *   - showAllWorkspaces is true (the toggle is on)
 *   - currentWorkspaceId is null (no workspace resolved yet)
 */
export function scopeCloudEnvironmentRows(
  rows: readonly CloudEnvironmentCatalogRow[],
  currentWorkspaceId: number | null,
  showAllWorkspaces: boolean,
): readonly CloudEnvironmentCatalogRow[] {
  if (showAllWorkspaces || currentWorkspaceId === null) return rows;
  return rows.filter((row) => row.cmmdOrganizationId === currentWorkspaceId);
}

/**
 * React hook: returns the pre-connect cloud project rows scoped to the current
 * workspace. Subscribes to workspace store changes so the sidebar re-renders
 * when the user switches workspaces without any prop change.
 */
export function useScopedPreConnectCloudRows(
  rows: readonly CloudProjectCatalogRow[],
): readonly CloudProjectCatalogRow[] {
  const { workspaceId } = useCurrentForgeWorkspaceState();
  return scopeCloudProjectRows(rows, workspaceId, false);
}
